/**
 * <airflux-wipe> — the drag-to-wipe windscreen.
 *
 * One number drives the whole thing: `--af-sweep`, 0 to 1, set on the host.
 * CSS turns it into both the blade's rotation and the conic mask that reveals
 * the clean layer, so the blade and the edge of the clean glass can never
 * disagree — they are the same angle read twice.
 *
 * The range input is not a fallback, it IS the control: it gives the section
 * keyboard operation, an accessible name and a value announcement for free.
 * Pointer dragging on the frame just writes to that same input and dispatches
 * the event, so there is one source of truth and no state to keep in step.
 *
 * The element is inert until upgraded: the markup ships resting at half-swept,
 * which already shows both states side by side. Nothing here is required to
 * understand the section — it only makes it playable.
 */
(() => {
  if (customElements.get('airflux-wipe')) return;

  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

  class AirfluxWipe extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('[data-wipe-frame]');
      this.range = this.querySelector('[data-wipe-range]');
      if (!this.frame || !this.range) return;

      this.reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onInput = () => this.apply(Number(this.range.value) / 100);
      this.range.addEventListener('input', this.onInput);

      // Pointer drag. `setPointerCapture` keeps the gesture alive when the
      // finger leaves the frame mid-sweep, which on a phone it always does —
      // the frame is only ~200px tall and the gesture is horizontal.
      this.onDown = (e) => {
        // Let the range keep its own native dragging; capturing here as well
        // would fight it and make the thumb jump.
        if (e.target === this.range) return;
        this.dragging = true;
        this.frame.setPointerCapture(e.pointerId);
        this.stopIntro();
        this.fromPointer(e);
      };
      this.onMove = (e) => {
        if (this.dragging) this.fromPointer(e);
      };
      this.onUp = (e) => {
        this.dragging = false;
        if (this.frame.hasPointerCapture(e.pointerId)) this.frame.releasePointerCapture(e.pointerId);
      };

      this.frame.addEventListener('pointerdown', this.onDown);
      this.frame.addEventListener('pointermove', this.onMove);
      this.frame.addEventListener('pointerup', this.onUp);
      this.frame.addEventListener('pointercancel', this.onUp);

      // A keyboard user reaching the slider should get the same invitation a
      // pointer user gets by hovering, and should not then be fighting an
      // animation that is still running.
      this.range.addEventListener('pointerdown', () => this.stopIntro());
      this.range.addEventListener('keydown', () => this.stopIntro());

      this.setAttribute('data-ready', '');
      this.apply(Number(this.range.value) / 100);
      this.scheduleIntro();
    }

    disconnectedCallback() {
      this.stopIntro();
      this.range?.removeEventListener('input', this.onInput);
      this.frame?.removeEventListener('pointerdown', this.onDown);
      this.frame?.removeEventListener('pointermove', this.onMove);
      this.frame?.removeEventListener('pointerup', this.onUp);
      this.frame?.removeEventListener('pointercancel', this.onUp);
      this.observer?.disconnect();
    }

    fromPointer(e) {
      const r = this.frame.getBoundingClientRect();
      if (!r.width) return;
      const v = clamp((e.clientX - r.left) / r.width, 0, 1);
      this.range.value = String(Math.round(v * 100));
      this.apply(v);
    }

    apply(v) {
      this.style.setProperty('--af-sweep', String(v));
    }

    /**
     * One sweep, the first time the section is actually looked at. It exists to
     * say "this moves" — a static half-wiped frame reads as a before/after
     * image, and nobody drags an image. It runs once, and any real input kills
     * it mid-flight so the reader never has to wait for it to finish.
     */
    scheduleIntro() {
      if (this.reduced || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((en) => en.isIntersecting)) return;
          this.observer.disconnect();
          this.playIntro();
        },
        { threshold: 0.55 }
      );
      this.observer.observe(this);
    }

    playIntro() {
      const rest = Number(this.range.value) / 100;
      const start = performance.now();
      const DELAY = 420;
      const DURATION = 1500;
      // Out to the left, then back across to where it started. The pause at the
      // far end is what makes it read as a wiper returning rather than a slider
      // sliding.
      const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

      const frame = (now) => {
        if (this.dragging || this.introStopped) return;
        const t = (now - start - DELAY) / DURATION;
        if (t < 0) {
          this.raf = requestAnimationFrame(frame);
          return;
        }
        if (t >= 1) {
          this.apply(rest);
          this.range.value = String(Math.round(rest * 100));
          this.introStopped = true;
          return;
        }
        // 0 → out to 0.02 → back to rest, as one pass.
        const p = ease(t);
        const v = p < 0.5 ? rest + (0.02 - rest) * (p / 0.5) : 0.02 + (rest - 0.02) * ((p - 0.5) / 0.5);
        this.apply(v);
        this.range.value = String(Math.round(v * 100));
        this.raf = requestAnimationFrame(frame);
      };

      this.raf = requestAnimationFrame(frame);
    }

    stopIntro() {
      this.introStopped = true;
      if (this.raf) cancelAnimationFrame(this.raf);
      this.observer?.disconnect();
    }
  }

  customElements.define('airflux-wipe', AirfluxWipe);
})();
